import type { RaceResult } from "../types/common";
import type { HorrorRaceState } from "../types/game-state";
import { findFinisher } from "./finish";
import { calculateRankings } from "./ranking";
import { calculateGarnetsFromRace, mergeGarnets } from "./scoring";

export interface RaceRecap {
  readonly raceResult: RaceResult;
  // Existing per-player garnet totals merged with this race's award.
  readonly garnets: Readonly<Record<string, number>>;
}

/**
 * Pure race-end tally. Computed once, when a race finishes (RACE_SCORING entry),
 * NOT when the host advances to the next race — so the recap screen has a stable
 * result to animate while every player confirms. Reads `state.characterPositions`
 * (already post-move at entry), the players' secret selections, and the running
 * garnet totals; returns the appended RaceResult plus the merged garnets. The
 * math (rankings → per-rank garnets → merge) is unchanged from the old
 * compute+advance `applyScoreRace`.
 */
export function computeRaceRecap(state: HorrorRaceState): RaceRecap {
  const rankings = calculateRankings(state.characterPositions);
  const finisher = findFinisher(
    state.characterPositions,
    state.config.trackLength,
  );

  const raceResult: RaceResult = {
    raceNumber: state.raceNumber,
    rankings,
    finisher,
  };

  const raceGarnets = calculateGarnetsFromRace(rankings, state.players);
  const garnets = mergeGarnets(state.garnets, raceGarnets);

  return { raceResult, garnets };
}
